import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AvatarPersona } from "@/components/ui/avatar-persona";
import { MODAL_FORM } from "@/components/ui/panel-widths";
import { FOTO_ACCEPT, FOTO_FORMATOS_TEXTO, useFotoPerfilUsuario, validarFotoPerfil } from "@/hooks/useFotoPerfilUsuario";
import { normalizeAvatarUrl } from "@/lib/avatarUrl";
import { cn } from "@/lib/utils";

/**
 * Avatar con su foto de perfil editable (`AvatarPersona` + `useFotoPerfilUsuario`; vive en admin/ porque sube a Storage y escribe `usuarios`): al tocarlo se
 * abre la foto completa (sin recorte) con "Cambiar foto" y "Eliminar foto"; sin foto, "Subir foto".
 * Una foto nueva se ve antes de guardarse; eliminar pide confirmación. La foto vive en
 * `usuarios.foto_perfil_url`, así que cambia en el admin, el Portal Agente y la app.
 */
export function AvatarEditable({
  nombre,
  fotoUrl,
  email,
  size = "xl",
  editable = true,
  className,
  onCambio,
}: {
  nombre: string | null | undefined;
  fotoUrl: string | null | undefined;
  /** Correo dueño de la foto, tal como está en `usuarios.email`. */
  email: string | null | undefined;
  size?: "md" | "lg" | "xl";
  editable?: boolean;
  className?: string;
  onCambio?: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [vistaPrevia, setVistaPrevia] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { subir, eliminar, subiendo, eliminando } = useFotoPerfilUsuario(email, onCambio);

  const foto = normalizeAvatarUrl(fotoUrl) || null;
  const ocupado = subiendo || eliminando;
  const puedeEditar = editable && !!email;

  // La URL de vista previa es un objeto en memoria: se libera siempre.
  useEffect(() => () => {
    if (vistaPrevia) URL.revokeObjectURL(vistaPrevia);
  }, [vistaPrevia]);

  const limpiar = () => {
    setArchivo(null);
    setVistaPrevia((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const cerrar = () => {
    if (ocupado) return;
    setAbierto(false);
    limpiar();
  };

  const elegir = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;
    const invalido = validarFotoPerfil(file);
    if (invalido) {
      toast.error(invalido);
      return;
    }
    limpiar();
    setArchivo(file);
    setVistaPrevia(URL.createObjectURL(file));
    setAbierto(true);
  };

  const guardar = async () => {
    if (!archivo) return;
    try {
      await subir(archivo);
      toast.success(foto ? "Foto de perfil actualizada" : "Foto de perfil agregada");
      setAbierto(false);
      limpiar();
    } catch (err: any) {
      toast.error(err?.message || "No se pudo subir la foto. Inténtalo de nuevo.");
    }
  };

  const borrar = async () => {
    try {
      await eliminar();
      toast.success("Foto de perfil eliminada");
      setConfirmarBorrar(false);
      setAbierto(false);
    } catch (err: any) {
      toast.error(err?.message || "No se pudo eliminar la foto.");
    }
  };

  const imagen = vistaPrevia ?? foto;

  return (
    <>
      <div className={cn("relative inline-flex shrink-0", className)}>
        <button
          type="button"
          onClick={() => (foto || puedeEditar ? setAbierto(true) : undefined)}
          disabled={!foto && !puedeEditar}
          aria-label={foto ? "Ver foto de perfil" : "Subir foto de perfil"}
          className="rounded-full outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-default"
        >
          <AvatarPersona nombre={nombre} fotoUrl={foto} size={size} />
        </button>
        {puedeEditar && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            aria-label={foto ? "Cambiar foto de perfil" : "Subir foto de perfil"}
            className="absolute -bottom-0.5 -right-0.5 flex size-7 items-center justify-center rounded-full border-2 border-background bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Camera className="size-3.5" aria-hidden />
          </button>
        )}
      </div>

      <input ref={inputRef} type="file" accept={FOTO_ACCEPT} className="hidden" onChange={elegir} />

      <Dialog open={abierto} onOpenChange={(v) => (v ? setAbierto(true) : cerrar())}>
        <DialogContent className={MODAL_FORM}>
          <DialogHeader>
            <DialogTitle>{archivo ? "Vista previa" : "Foto de perfil"}</DialogTitle>
            <DialogDescription>
              {archivo ? "Así se verá tu foto. En los avatares se recorta en círculo." : nombre || "Foto de perfil"}
            </DialogDescription>
          </DialogHeader>

          <div className="flex min-h-[220px] items-center justify-center rounded-lg bg-muted/40 p-2">
            {imagen ? (
              <img
                src={imagen}
                alt={archivo ? "Vista previa de la foto de perfil" : `Foto de ${nombre ?? "perfil"}`}
                className="max-h-[50vh] w-auto max-w-full rounded-md object-contain"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-center">
                <AvatarPersona nombre={nombre} size="xl" />
                <p className="text-[12.5px] text-muted-foreground">Aún no tienes foto. Se muestran tus iniciales.</p>
              </div>
            )}
          </div>
          {puedeEditar && !archivo && <p className="text-[12px] text-muted-foreground">{FOTO_FORMATOS_TEXTO}</p>}

          {puedeEditar && (
            <DialogFooter className="gap-2 sm:justify-between">
              {archivo ? (
                <>
                  <Button variant="cancel" size="compact" onClick={limpiar} disabled={subiendo}>
                    Elegir otra
                  </Button>
                  <Button variant="primary-outline" size="compact" onClick={guardar} disabled={subiendo}>
                    {subiendo ? <><Loader2 className="animate-spin" /> Guardando...</> : "Guardar foto"}
                  </Button>
                </>
              ) : (
                <>
                  {foto ? (
                    <Button
                      variant="ghost"
                      size="compact"
                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => setConfirmarBorrar(true)}
                      disabled={ocupado}
                    >
                      <Trash2 /> Eliminar foto
                    </Button>
                  ) : (
                    <span />
                  )}
                  <Button variant="primary-outline" size="compact" onClick={() => inputRef.current?.click()} disabled={ocupado}>
                    <Upload /> {foto ? "Cambiar foto" : "Subir foto"}
                  </Button>
                </>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmarBorrar} onOpenChange={(v) => !eliminando && setConfirmarBorrar(v)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar tu foto de perfil?</AlertDialogTitle>
            <AlertDialogDescription>
              Se mostrarán tus iniciales en el admin, el Portal Agente y la app.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eliminando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void borrar();
              }}
              disabled={eliminando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminando ? "Eliminando..." : "Eliminar foto"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
